/**
 * Titles and yichus designations, told apart from the names they sit beside.
 *
 * A name field holds a name. What precedes it is a title (`person.honorific_he`
 * or the shul's default), and `הכהן` / `הלוי` is a yichus assertion belonging in
 * `person.yichus` — so both are composed at render time from the column that
 * owns them, and neither is ever written into a name.
 *
 * Two callers depend on that being ONE rule: the importer, which strips on the
 * way in, and the renderer, which must not prefix a title onto something that
 * already carries one. Split across two implementations they drift, and the
 * result is a name printed with two titles or none.
 */

/**
 * Hebrew punctuation comes in a typographic form and a typewriter form, and
 * source data carries both — `ר׳` with U+05F3 GERESH and `ר'` with an
 * apostrophe, `מו״ה` with U+05F4 GERSHAYIM and `מו"ה` with a quote. Matching one
 * form silently leaves half the roster untouched.
 */
const G = "['׳’]" // geresh: apostrophe, ׳, ’
const GG = '["״]' // gershayim: quote, ״

/**
 * The titles that can precede a name, longest first — `הרב` must not match
 * inside `מורינו הרב` and leave the first word stranded as part of the name.
 */
const TITLE_FORMS = [
  `מורינו\\s+הרב`,
  `מורנו\\s+הרב`,
  `הרה${GG}ג`,
  `מוה${GG}ר`,
  `מו${GG}ה`,
  `ה${GG}ר`,
  `הר${G}`,
  `הרב`,
  `הבחור`,
  `הגאון`,
  `רב`,
  `ר${G}`,
].join('|')

/** A title at the very start of a value, with the name following it. */
const LEADING_TITLE = new RegExp(`^\\s*(${TITLE_FORMS})\\s+`)

/** A value that is a title and NOTHING else — a name field with no name in it. */
const ONLY_TITLE = new RegExp(`^\\s*(${TITLE_FORMS})\\s*$`)

/** A title anywhere it can legitimately stand: at the start, or after בן / בת. */
const ANY_TITLE = new RegExp(`(^|\\s(?:בן|בת)\\s+)(${TITLE_FORMS})\\s+`, 'g')

/**
 * `הכהן` / `הלוי` as a standalone word.
 *
 * Deliberately NOT applied to a family name: `הלוי` is also a surname, and there
 * is no rule that can tell the surname Halevi from the designation. In a given
 * name, a father's name or an aliya name it is the designation.
 */
const DESIGNATION = /(^|\s)(הכהן|הלוי)(?=$|\s)/g

export type DesignatedYichus = 'kohen' | 'levi'

const DESIGNATION_YICHUS: Record<string, DesignatedYichus> = {
  הכהן: 'kohen',
  הלוי: 'levi',
}

const tidy = (value: string) => value.replace(/\s+/g, ' ').trim()

/** Whether a value already opens with a title, so nothing should prefix it. */
export function hasHonorific(value: string | null | undefined): boolean {
  return !!value && LEADING_TITLE.test(value)
}

/**
 * The title a value opens with, and the value without it. `honorific` is null
 * when it opens with a name, which is the state every name field should be in.
 */
export function splitHonorific(value: string | null | undefined): {
  honorific: string | null
  rest: string
} {
  const raw = value?.trim() ?? ''
  const match = raw.match(LEADING_TITLE)
  if (!match) return { honorific: null, rest: raw }
  return { honorific: tidy(match[1]), rest: tidy(raw.slice(match[0].length)) }
}

/**
 * The title a value consists of, when it holds nothing else.
 *
 * A name field with only a title in it holds no name: it is what an import
 * leaves behind when the source cell had the honorific and the name was never
 * filled in. It cannot be read by `splitHonorific`, which requires a name after
 * the title — and read as a name it composes into `בן ר׳ ר׳`, a patronymic
 * naming nobody.
 *
 * The title is still the person's (or the father's) and is claimed by whoever
 * asks; what must not survive is the NAME field, which should be empty.
 */
export function onlyHonorific(value: string | null | undefined): string | null {
  const match = value?.match(ONLY_TITLE)
  return match ? tidy(match[1] as string) : null
}

/**
 * Every title removed, including one inside a `בן …` segment — which is where
 * half of them are, since an imported aliya name names the father too.
 */
export function stripHonorifics(value: string | null | undefined): string {
  if (!value) return ''
  return tidy(value.replace(ANY_TITLE, '$1'))
}

/**
 * The yichus a value asserts, and the value with the designation removed.
 *
 * Whichever position it appears in, it asserts the yichus of the person the
 * value describes: yichus is patrilineal, so a father designated `הלוי` inside
 * his son's aliya name makes the son a levi just as directly.
 */
export function splitYichusDesignation(value: string | null | undefined): {
  designation: string | null
  yichus: DesignatedYichus | null
  rest: string
} {
  const raw = value?.trim() ?? ''
  const found = [...raw.matchAll(DESIGNATION)].map((m) => m[2])
  if (found.length === 0) return { designation: null, yichus: null, rest: raw }
  const designation = found[0] as string
  return {
    designation,
    yichus: DESIGNATION_YICHUS[designation] ?? null,
    rest: tidy(raw.replace(DESIGNATION, '$1')),
  }
}

/**
 * A name field reduced to the name: no title, no designation.
 *
 * A field that was ONLY a title reduces to nothing, which is the honest answer:
 * it never held a name. Read as one it composes into `בן ר׳ ר׳` — a patronymic
 * naming nobody — and prints that on the sheet the gabbai calls from.
 */
export function bareName(value: string | null | undefined): string {
  if (onlyHonorific(value)) return ''
  return splitYichusDesignation(stripHonorifics(value)).rest
}

/**
 * `<prefix> <name>`, where the prefix is skipped if the name carries its own
 * title or if the shul prefixes nothing. Composition, never storage.
 */
export function withHonorific(
  name: string | null | undefined,
  prefix: string | null | undefined,
): string {
  const trimmed = name?.trim() ?? ''
  if (!trimmed) return ''
  if (!prefix?.trim() || hasHonorific(trimmed)) return trimmed
  return `${prefix.trim()} ${trimmed}`
}
